// Acquisition attribution: first-touch (how they discovered us) + last non-direct touch (what
// brought them back to convert). Both are captured client-side and attached to orders, so the
// dashboard can credit the source that actually drove each purchase.
//
// Why two touches: a customer might first arrive from an organizer's site, leave, then come back
// days later from an email reminder and buy. First-touch keeps "organizer site"; last-touch updates
// to "email" on the return visit. Capturing at the start of each visit (not at checkout) is what
// stops the storefront's own pages from masquerading as the source.

const FIRST_KEY = "__authentigate_t1_first_touch";
const LAST_KEY = "__authentigate_t1_last_touch";
const FIRST_TOUCH_WINDOW_MS = 90 * 24 * 60 * 60 * 1000; // discovery window
const LAST_TOUCH_CARRY_MS = 30 * 24 * 60 * 60 * 1000; // last-non-direct carry window

type Touch = {
    referrer: string;
    utmSource: string;
    utmMedium: string;
    utmCampaign: string;
    utmTerm: string;
    utmContent: string;
    gclid: string;
    fbclid: string;
    landingPage: string;
    at: string;
};

const readTouch = (key: string): Touch | null => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? (JSON.parse(raw) as Touch) : null;
    } catch {
        return null;
    }
};

const writeTouch = (key: string, touch: Touch): void => {
    try {
        localStorage.setItem(key, JSON.stringify(touch));
    } catch {
        // localStorage unavailable (private mode / blocked) — attribution is best-effort.
    }
};

// Keep the referrer only when it points somewhere other than our own site. Empty or same-host means
// the visit was direct/internal, so we record nothing for it.
const externalReferrer = (): string => {
    const ref = document.referrer;
    if (!ref) return "";
    try {
        return new URL(ref).host === window.location.host ? "" : ref;
    } catch {
        return "";
    }
};

const buildCurrentTouch = (): Touch => {
    const params = new URLSearchParams(window.location.search);
    return {
        referrer: externalReferrer(),
        utmSource: params.get("utm_source") ?? "",
        utmMedium: params.get("utm_medium") ?? "",
        utmCampaign: params.get("utm_campaign") ?? "",
        utmTerm: params.get("utm_term") ?? "",
        utmContent: params.get("utm_content") ?? "",
        gclid: params.get("gclid") ?? "",
        fbclid: params.get("fbclid") ?? "",
        landingPage: window.location.pathname,
        at: String(Date.now()),
    };
};

// A touch is "identifiable" if it carries an external referrer, a UTM source, or an ad click id.
const hasSource = (touch: Touch): boolean =>
    touch.referrer !== "" ||
    touch.utmSource !== "" ||
    touch.gclid !== "" ||
    touch.fbclid !== "";

const isFresh = (touch: Touch | null, windowMs: number): boolean => {
    const at = Number(touch?.at);
    return Number.isFinite(at) && Date.now() - at < windowMs;
};

/**
 * Record attribution for the current page load. Safe to call on every load.
 * - First-touch: captured once, then preserved for the discovery window (90 days).
 * - Last-touch: updated whenever THIS visit has an identifiable source (so a return from an email or
 *   another site is tracked). On a direct/internal visit it keeps the previous non-direct source if
 *   it's within the 30-day carry window, otherwise it becomes Direct (last-non-direct attribution).
 */
export const captureAttribution = (): void => {
    if (typeof window === "undefined") return;
    const current = buildCurrentTouch();

    if (!isFresh(readTouch(FIRST_KEY), FIRST_TOUCH_WINDOW_MS)) {
        writeTouch(FIRST_KEY, current);
    }

    const existingLast = readTouch(LAST_KEY);
    if (hasSource(current)) {
        writeTouch(LAST_KEY, current);
    } else if (
        existingLast &&
        hasSource(existingLast) &&
        Date.now() - Number(existingLast.at) <= LAST_TOUCH_CARRY_MS
    ) {
        // Direct visit, but a recent non-direct source still owns the credit — keep it.
    } else {
        writeTouch(LAST_KEY, current); // Direct (no recent identifiable source)
    }
};

const prefixed = (
    prefix: "first" | "last",
    touch: Touch,
): { [key: string]: string } => ({
    [`${prefix}Referrer`]: touch.referrer,
    [`${prefix}UtmSource`]: touch.utmSource,
    [`${prefix}UtmMedium`]: touch.utmMedium,
    [`${prefix}UtmCampaign`]: touch.utmCampaign,
    [`${prefix}UtmTerm`]: touch.utmTerm,
    [`${prefix}UtmContent`]: touch.utmContent,
    [`${prefix}Gclid`]: touch.gclid,
    [`${prefix}Fbclid`]: touch.fbclid,
    [`${prefix}LandingPage`]: touch.landingPage,
    [`${prefix}TouchAt`]: touch.at,
});

/**
 * First- and last-touch attribution as a flat string map for order metadata. Keys are prefixed
 * `first*` / `last*`; empty fields are dropped. Returns {} when nothing was captured.
 */
export const getAttributionMetadata = (): { [key: string]: string } => {
    if (typeof window === "undefined") return {};
    const first = readTouch(FIRST_KEY);
    const last = readTouch(LAST_KEY);
    const merged = {
        ...(first ? prefixed("first", first) : {}),
        ...(last ? prefixed("last", last) : {}),
    };
    return Object.fromEntries(
        Object.entries(merged).filter(([, value]) => value !== ""),
    );
};
